import { useEffect, useRef, useState } from "react";
import { ArrowLeft, ArrowRight, BookOpen, Check, CirclePlay, ExternalLink, FileText, ListChecks, LockKeyhole } from "lucide-react";
import { Badge, Button, Dialog, DialogContent, DialogTitle, FileViewerPanel, Progress } from "@codelittinc/carbon-design-system";
import type { TrainingItem, TrainingTrack } from "@carbon-os/server/contract";
import { trackTraining, type TrainingEvents, type TrainingStepKind } from "../lib/analytics";
import { canTakeQuiz, useQuiz } from "../lib/quiz";
import { isHttpUrl } from "../lib/tools";
import { celebrate, progressOf, type Done, type TrainingControls } from "../lib/training";
import { useVideo } from "../lib/video";
import { ProcessStep } from "./ProcessStep";
import { Quiz } from "./Quiz";
import { Video } from "./Video";

/** What the address bar says is open: one per kind of step, so a link reopens it. */
export const STEP_RESOURCES = ["video", "quiz", "file", "process", "step"] as const;
export type StepResource = (typeof STEP_RESOURCES)[number];

/** The resource a step opens as: its own kind, or `step` for a link video or a missing file. */
export function resourceOf(item: TrainingItem, quiz = false): StepResource {
  if (quiz) return "quiz";
  if (item.itemType === "process") return "process";
  if (item.video) return "video";
  if (item.kind === "file" && item.url) return "file";
  return "step";
}

/** How a step shows, for analytics: unlike `resourceOf`, a link video and a missing file are told apart. */
export function stepKind(item: TrainingItem, quiz = false): TrainingStepKind {
  if (quiz) return "quiz";
  if (item.itemType === "process") return "process";
  if (item.video) return "video";
  if (item.kind === "file") return item.url ? "file" : "unavailable";
  return isHttpUrl(item.url) ? "link_video" : "unavailable";
}

type SelectedVia = TrainingEvents["training_step_selected"]["via"];

interface Step {
  item: TrainingItem;
  quiz: boolean;
}

/** A step is left to do while it is not done and can still be done. */
function open(step: Step, done: Done): boolean {
  if (done[step.item.materialId] || step.item.locked) return false;
  return step.quiz ? canTakeQuiz(step.item) : true;
}

/**
 * A track, one step at a time, with every step listed on the right: videos,
 * files, handbook processes, link videos and quizzes. Finishing a step moves on
 * to the next one still to do — a tick, an acknowledgment, a video that played
 * to its end — except after a quiz, whose result stays up with Next step.
 */
export function TrackPlayer({ track, initialItem, initialQuiz, done, training, viewerKey, onClose, onCloseAutoFocus, onToggle, onStepChange, onProcessAcknowledged }: {
  track: TrainingTrack;
  initialItem: TrainingItem;
  initialQuiz: boolean;
  done: Done;
  training: TrainingControls;
  viewerKey: string | null;
  onClose: () => void;
  onCloseAutoFocus: (event: Event) => void;
  /** The page's own tick, so the announcement and a finished track's celebration happen here too. */
  onToggle: (item: TrainingItem) => void;
  /** Keeps the address bar on the step showing. */
  onStepChange: (item: TrainingItem, resource: StepResource) => void;
  onProcessAcknowledged: () => void;
}) {
  const [selection, setSelection] = useState({ itemId: initialItem.materialId, quiz: initialQuiz, visit: 0 });
  const [activeQuiz, setActiveQuiz] = useState(initialQuiz && canTakeQuiz(initialItem));
  const selected = selection.itemId;
  const item = track.items.find((entry) => entry.materialId === selected) ?? initialItem;
  const steps: Step[] = track.items.flatMap((entry) => [
    { item: entry, quiz: false },
    ...(entry.quiz ? [{ item: entry, quiz: true }] : []),
  ]);
  const { done: count, total } = progressOf(track, done);

  /** `via` only when the person picked it: moving on by itself after a tick, a video's end or an acknowledgment counts nothing. */
  const select = (next: Step, via?: SelectedVia) => {
    if (next.item.materialId === selected && next.quiz === activeQuiz) return;
    if (via) {
      trackTraining("training_step_selected", {
        track_id: track.id,
        material_id: next.item.materialId,
        step_kind: stepKind(next.item, next.quiz),
        via,
      });
    }
    setActiveQuiz(next.quiz);
    setSelection((current) => ({ itemId: next.item.materialId, quiz: next.quiz, visit: current.visit + 1 }));
    onStepChange(next.item, resourceOf(next.item, next.quiz));
  };
  const here = steps.findIndex((step) => step.item.materialId === selected && step.quiz === activeQuiz);
  // The next step still to do after this one, then round from the start: one
  // opened late in the list still leads to what is left. `done` is read when it
  // is called, after a tick has already flipped it.
  const nextStep = (now: Done = done) =>
    [...steps.slice(here + 1), ...steps.slice(0, Math.max(here, 0))].find((step) => open(step, now)) ?? null;
  const advance = (now?: Done, via?: SelectedVia) => {
    const next = nextStep(now);
    if (next) select(next, via);
  };

  return (
    <Dialog open onOpenChange={(isOpen) => { if (!isOpen) onClose(); }}>
      <DialogContent aria-describedby={undefined} className="inset-0 top-0 left-0 h-dvh max-h-none max-w-none translate-x-0 translate-y-0 gap-0 overflow-hidden rounded-none border-0 p-0 data-[state=open]:animate-none data-[state=closed]:animate-none" onCloseAutoFocus={onCloseAutoFocus}>
        <header className="flex shrink-0 items-center gap-4 border-b border-border-subtle px-4 py-3 pr-14 sm:px-6">
          <Button variant="ghost" className="h-11 shrink-0" onClick={onClose}>
            <ArrowLeft className="size-4" aria-hidden /> Back to training
          </Button>
          <DialogTitle className="min-w-0 truncate">{track.name}</DialogTitle>
        </header>
        <div className="min-h-0 flex-1 overflow-y-auto lg:grid lg:grid-cols-[minmax(0,1fr)_22rem] lg:overflow-hidden">
          <section aria-label="Step" className="min-w-0 p-4 sm:p-6 lg:overflow-y-auto lg:p-8">
            <Lesson key={`${viewerKey}:${item.materialId}:${selection.visit}`} item={item} trackId={track.id} training={training}
              viewerKey={viewerKey} initialQuiz={selection.quiz} onQuizChange={setActiveQuiz}
              completed={Boolean(done[item.materialId])}
              hasNext={nextStep() !== null}
              onNext={(via) => advance(undefined, via)}
              onTick={() => {
                const finishing = !done[item.materialId];
                onToggle(item);
                if (finishing) advance({ ...done, [item.materialId]: "now" });
              }}
              onAcknowledged={(completedAt) => {
                // Refused because the track ended: re-read, so the step locks.
                if (completedAt === null) return onProcessAcknowledged();
                // Done at once, in every track holding the process; each one it finished celebrates.
                for (const finished of training.acknowledged(item.processId ?? item.materialId, completedAt)) celebrate(finished);
                advance({ ...done, [item.materialId]: completedAt });
              }} />
          </section>
          <aside aria-label="Track steps" className="border-t border-border-subtle bg-surface lg:overflow-y-auto lg:border-t-0 lg:border-l">
            <div className="border-b border-border-subtle p-5">
              <h2 className="font-semibold">In this track</h2>
              <p className="mt-1 text-sm text-text-secondary">{steps.length} steps · {count} of {total} items done</p>
              <Progress value={count} max={total} aria-label="Track completion" className="mt-3" />
            </div>
            <ol className="space-y-2 p-3">
              {steps.map((step, index) => (
                <li key={`${step.item.materialId}:${step.quiz ? "quiz" : "material"}`}>
                  <StepButton step={step} number={index + 1} done={done}
                    current={step.item.materialId === selected && step.quiz === activeQuiz}
                    onSelect={() => select(step, "sidebar")} />
                </li>
              ))}
            </ol>
          </aside>
        </div>
      </DialogContent>
    </Dialog>
  );
}

/** What the list says under a step's title. */
function stepStatus(step: Step, done: Done, current: boolean): string {
  const { item } = step;
  const complete = Boolean(done[item.materialId]);
  if (step.quiz) {
    const questions = item.quiz?.questionCount ?? 0;
    const state = complete ? "Completed" : !canTakeQuiz(item) ? "Track ended" : current ? "Now viewing" : "Ready to take";
    return `${questions} ${questions === 1 ? "question" : "questions"} · ${state}`;
  }
  if (complete) return "Completed";
  if (current) return "Now viewing";
  if (item.itemType === "process") return item.needsReread && item.previousVersion ? "Updated · read again" : "Handbook · to read";
  if (item.video) return `${Math.round(item.video.watchedPercent)}% watched`;
  if (item.kind === "video") return isHttpUrl(item.url) ? "External video" : "Video unavailable";
  return item.url ? "File" : "File unavailable";
}

function StepButton({ step, number, done, current, onSelect }: {
  step: Step;
  number: number;
  done: Done;
  current: boolean;
  onSelect: () => void;
}) {
  const { item } = step;
  const complete = Boolean(done[item.materialId]);
  const locked = step.quiz && !canTakeQuiz(item);
  const KindIcon = step.quiz ? ListChecks : item.itemType === "process" ? BookOpen : item.kind === "video" ? CirclePlay : FileText;
  return (
    <button type="button" aria-current={current ? "true" : undefined} disabled={locked}
      aria-label={step.quiz ? `Quiz: ${item.title}` : undefined}
      onClick={onSelect}
      className={`flex w-full items-start gap-3 rounded-lg p-3 text-left focus-visible:outline-2 focus-visible:outline-accent disabled:cursor-not-allowed disabled:opacity-60 ${current ? "bg-accent-muted text-accent-text" : "hover:bg-surface-overlay"}`}>
      <span className="flex size-8 shrink-0 items-center justify-center rounded-full bg-bg text-sm">
        {locked ? <LockKeyhole className="size-4" aria-hidden /> : complete ? <Check className="size-4" aria-hidden /> : current ? <KindIcon className="size-4" aria-hidden /> : number}
      </span>
      <span className="min-w-0 flex-1">
        <span className="block break-words text-sm font-medium">{step.quiz ? "Quiz" : item.title}</span>
        <span className="mt-1 block text-xs text-text-secondary">{stepStatus(step, done, current)}</span>
      </span>
    </button>
  );
}

/** A separate session per step lets the departing player flush its final ranges to the right material. */
function Lesson({ item, trackId, training, viewerKey, initialQuiz, completed, hasNext, onNext, onTick, onAcknowledged, onQuizChange }: {
  item: TrainingItem;
  trackId: string;
  training: TrainingControls;
  viewerKey: string | null;
  initialQuiz: boolean;
  completed: boolean;
  hasNext: boolean;
  /** `via` when "Next step" was pressed; none when the player moves on by itself. */
  onNext: (via?: SelectedVia) => void;
  onTick: () => void;
  onAcknowledged: (completedAt: string | null) => void;
  onQuizChange: (open: boolean) => void;
}) {
  const video = useVideo({ ...training, viewerKey });
  const quiz = useQuiz(training);
  const [ended, setEnded] = useState(false);
  const heading = useRef<HTMLHeadingElement>(null);
  const initial = useRef({ item, trackId, initialQuiz, openVideo: video.open, openQuiz: quiz.open, closeVideo: video.close, closeQuiz: quiz.close });
  useEffect(() => {
    const session = initial.current;
    if (session.initialQuiz && canTakeQuiz(session.item)) session.openQuiz(session.item, session.trackId);
    else if (session.item.video) session.openVideo(session.item, session.trackId);
    heading.current?.focus();
    return () => { session.closeVideo(); session.closeQuiz(); };
  }, []);
  // A ref, so StrictMode's second effect run counts nothing more; each step is a new Lesson.
  const processOpened = useRef(false);
  useEffect(() => {
    const { item: step, trackId: stepTrack } = initial.current;
    if (processOpened.current || step.itemType !== "process") return;
    processOpened.current = true;
    trackTraining("training_item_opened", { material_id: step.materialId, track_id: stepTrack, kind: "process" });
  }, []);

  const showQuiz = quiz.view.kind !== "closed";
  const unlocked = canTakeQuiz(item);
  useEffect(() => { onQuizChange(showQuiz); }, [showQuiz, onQuizChange]);
  // A video with a quiz that played to its end: on to the quiz.
  useEffect(() => {
    if (!ended || !unlocked || !item.quiz || completed || showQuiz) return;
    setEnded(false);
    video.close();
    quiz.open(item, trackId);
  }, [ended, unlocked, item, completed, showQuiz, video.close, quiz.open, trackId]);
  // A video with no quiz that played to its end, once Gatekeeper counts it: on to the next step.
  const next = useRef(onNext);
  next.current = onNext;
  useEffect(() => {
    if (ended && completed && !item.quiz) next.current();
  }, [ended, completed, item.quiz]);
  const hadQuiz = useRef(showQuiz);
  useEffect(() => {
    if (hadQuiz.current && !showQuiz) heading.current?.focus();
    hadQuiz.current = showQuiz;
  }, [showQuiz]);
  const takeQuiz = () => {
    if (!unlocked) return;
    video.close();
    setEnded(false);
    quiz.open(item, trackId);
  };
  const replay = () => {
    quiz.close();
    setEnded(false);
    video.open(item, trackId);
    heading.current?.focus();
  };

  const process = item.itemType === "process";
  const file = !process && item.kind === "file" && !item.video;
  const link = !process && item.kind === "video" && !item.video && isHttpUrl(item.url) ? item.url : null;
  // Ticked by hand: a file or a link video with no quiz, as on its row.
  const tickable = !process && !item.video && !item.quiz;

  let content;
  if (showQuiz) content = <div className="overflow-hidden rounded-xl border border-border-subtle bg-surface"><Quiz quiz={quiz} inline /></div>;
  else if (process) content = <ProcessStep item={item} trackId={trackId} onAcknowledged={onAcknowledged} />;
  else if (video.view.kind !== "closed") content = <div className="overflow-hidden rounded-xl border border-border-subtle bg-surface"><Video video={video} onEnded={() => setEnded(true)} /></div>;
  else if (file && item.url) content = <FileViewerPanel url={item.url} filename={item.title} className="h-[65dvh]" />;
  else {
    content = (
      <div className="flex min-h-72 flex-col items-center justify-center gap-4 rounded-xl border border-border-subtle bg-surface p-6 text-center">
        {link ? (
          <>
            <p className="text-text-secondary">This video opens on its own site.</p>
            <Button asChild variant="outline">
              <a href={link} target="_blank" rel="noreferrer"
                onClick={() => trackTraining("training_item_opened", { material_id: item.materialId, track_id: trackId, kind: "video" })}>
                Open video <ExternalLink className="size-4" aria-hidden /><span className="sr-only">(opens in a new tab)</span>
              </a>
            </Button>
          </>
        ) : item.video ? (
          <>
            <p className="text-text-secondary">{completed ? "You've completed this item." : "Ready to continue?"}</p>
            <Button variant="outline" onClick={replay}>Watch again</Button>
          </>
        ) : (
          <p className="text-text-secondary">{file ? "This file isn't available." : "This video's link isn't available."}</p>
        )}
      </div>
    );
  }

  return (
    <div className="mx-auto w-full max-w-6xl">
      <div className="mb-5 flex items-start justify-between gap-3">
        <h1 ref={heading} tabIndex={-1} className="text-xl font-semibold break-words outline-none sm:text-2xl">{item.title}</h1>
        {completed ? <Badge variant="success">Completed</Badge> : null}
      </div>
      {content}
      <div className="mt-5 flex flex-wrap items-center gap-3">
        {showQuiz && item.video ? <Button variant="outline" onClick={replay}>Back to video</Button> : null}
        {!showQuiz && item.quiz && !completed && unlocked ? (
          <Button onClick={takeQuiz}>{item.quiz.inProgress ? "Resume quiz" : "Take quiz"}</Button>
        ) : null}
        {tickable && !item.locked ? (
          completed
            ? <Button variant="ghost" onClick={onTick}>Mark as not done</Button>
            : <Button onClick={onTick}><Check className="size-4" aria-hidden />Mark as done</Button>
        ) : null}
        {tickable && item.locked && !completed ? (
          <p className="text-sm text-text-secondary">This track has ended: you can still open this, but it can't be marked done.</p>
        ) : null}
        {hasNext && (completed || (ended && !item.quiz) || (quiz.view.kind === "result" && quiz.view.result.passed)) ? (
          <Button className="ml-auto" onClick={() => onNext("next_button")}>Next step <ArrowRight className="size-4" aria-hidden /></Button>
        ) : null}
      </div>
    </div>
  );
}
